
import { CaseStudy } from "./caseStudyTypes";

export const caseStudies: CaseStudy[] = [
  {
    id: "OptimizedMicrosoft365SecurityTransformation",
    title: "Financial Services Firm Optimizes Microsoft 365 Security",
    industry: "Financial Services",
    companyType: "500+ Employees",
    challenge: "A financial services firm with premium Microsoft 365 E5 licenses was only using 15% of available security features while maintaining 14 redundant security tools.",
    solution: "Implemented Microsoft Defender suite with automated response playbooks",
    results: [
      { text: "40% improvement in NIST CSF score" },
      { text: "Consolidated 14 security tools down to 5, saving $320K annually" },
      { text: "Created unified security dashboards in Microsoft Sentinel" },
      { text: "85% reduction in security alerts" }
    ],
    testimonial: {
      quote: "What surprised us most was that by optimizing our Microsoft 365 security stack, we not only saved money but actually improved our security posture beyond what our previous patchwork of tools provided.",
      author: "CFO",
      role: "Financial Services Firm"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/OptimizedMicrosoft365SecurityTransformation"
    },
    tags: ["Microsoft 365", "Financial Services", "Cost Optimization", "Security Tools", "NIST CSF"],
    featured: true,
    detailedPage: true,
    redactedName: "Financial Enterprise Group"
  },
  {
    id: "biomedtech-security-transformation",
    title: "Healthcare Provider Accelerates Growth with Strategic Security",
    industry: "Healthcare",
    companyType: "250+ Employees",
    challenge: "A rapidly growing healthcare provider needed to expand while maintaining strict HIPAA compliance and protecting sensitive patient data across new locations.",
    solution: "Implemented virtual CISO services with compliance framework development",
    results: [
      { text: "Achieved HIPAA compliance across all new locations" },
      { text: "Developed a unified compliance framework for HIPAA and SOC 2 requirements" },
      { text: "Created security enablement processes for new clinic openings" },
      { text: "Reduced security implementation time for new locations by 60%" }
    ],
    testimonial: {
      quote: "edgefi transformed security from a growth blocker to a growth enabler. Their strategic advisory has been instrumental in our expansion while keeping patient data safe.",
      author: "Michael R.",
      role: "CEO, Regional Healthcare Group"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/biomedtech-security-transformation"
    },
    tags: ["Healthcare", "Compliance", "HIPAA", "Strategic Security", "Virtual CISO"],
    featured: true,
    detailedPage: true,
    redactedName: "Northwest Medical Partners"
  },
  {
    id: "RegionalRestaurantChainTransformation",
    title: "Regional Restaurant Chain Modernizes IT Infrastructure",
    industry: "Hospitality",
    companyType: "800+ Employees",
    challenge: "A regional restaurant chain with 30+ locations was struggling with legacy on-premises systems that were expensive to maintain and created security gaps across their distributed workforce.",
    solution: "Comprehensive Microsoft 365 migration with security-first approach",
    results: [
      { text: "Migrated 30+ locations to Microsoft 365 with zero business disruption" },
      { text: "Increased Microsoft Secure Score from 45 to 145 points" },
      { text: "Reduced annual security spending by 30% through consolidation" },
      { text: "Implemented standardized security policies across all locations" }
    ],
    testimonial: {
      quote: "The migration was seamless, and our team now has secure access to everything they need from any location. We've finally eliminated our dependency on outdated servers while actually improving security.",
      author: "Operations Director",
      role: "Regional Restaurant Chain"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/RegionalRestaurantChainTransformation"
    },
    tags: ["Hospitality", "Microsoft 365", "Migration", "Multi-location", "Cost Reduction"],
    featured: false,
    detailedPage: true,
    redactedName: "Coastal Dining Collective"
  },
  {
    id: "OregonSkiResortSecuresSeasonal",
    title: "Oregon Ski Resort Secures Seasonal Operations",
    industry: "Recreation & Tourism",
    companyType: "200+ Employees",
    challenge: "An Oregon ski resort faced unique security challenges with seasonal staff turnover, weather-dependent operations, and the need to protect customer payment information across multiple mountain facilities.",
    solution: "Customized Microsoft 365 security with seasonal worker provisions",
    results: [
      { text: "Implemented automated seasonal staff onboarding and offboarding security processes" },
      { text: "Protected customer payment data with advanced encryption and compliance controls" },
      { text: "Deployed weather-resistant backup solutions with 99.9% reliability" },
      { text: "Reduced IT security incidents by 75% during peak season" }
    ],
    testimonial: {
      quote: "Our business operates differently than most, with massive seasonal swings and challenging environments. edgefi understood these unique needs and built security that works with our business, not against it.",
      author: "IT Director",
      role: "Pacific Northwest Ski Resort"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/OregonSkiResortSecuresSeasonal"
    },
    tags: ["Recreation", "Tourism", "Microsoft 365", "Identity Management", "Seasonal Business"],
    featured: false,
    detailedPage: true,
    redactedName: "Cascade Mountain Resort"
  },
  {
    id: "TechCompanyInfrastructureScaling",
    title: "Software Company Transforms Microsoft 365 Environment",
    industry: "Technology",
    companyType: "80+ Employees",
    challenge: "A growing software company struggled with inconsistent Microsoft 365 configurations, security gaps, and collaboration inefficiencies that were hampering productivity and preventing them from meeting growth targets.",
    solution: "Comprehensive Microsoft 365 optimization with security-first approach",
    results: [
      { text: "Increased Microsoft Secure Score from 38 to 172 points" },
      { text: "Reduced user-reported IT issues by 76% through improved system reliability" },
      { text: "Implemented advanced security controls saving 32% on separate security tools" },
      { text: "Accelerated team collaboration by standardizing Microsoft Teams architecture" }
    ],
    testimonial: {
      quote: "Our Microsoft 365 environment was holding back our growth. With edgefi optimizing our systems, our team can focus entirely on building products that customers love instead of fighting with inconsistent tools and unreliable security.",
      author: "VP of Engineering",
      role: "B2B Software Company"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/TechCompanyInfrastructureScaling"
    },
    tags: ["Technology", "Microsoft 365", "Cloud Security", "Productivity", "Managed Services"],
    featured: false,
    detailedPage: true,
    redactedName: "Velocity Software Solutions"
  },
  {
    id: "TechStartupScalesSecurity",
    title: "BioMed Tech Transforms Security Chaos into Business Growth",
    industry: "Healthcare Technology",
    companyType: "250+ Employees",
    challenge: "BioMed Tech was facing a perfect storm of security and compliance challenges including failed security reviews that were actively preventing business growth and delaying enterprise deals.",
    solution: "Strategic Security Framework with Executive Leadership",
    results: [
      { text: "Achieved SOC 2 compliance in 90 days" },
      { text: "Secured 62% increase in enterprise deals after implementing security improvements" },
      { text: "Reduced security decision-making time from weeks to days" },
      { text: "Established security as a business enabler rather than a blocker" }
    ],
    testimonial: {
      quote: "Our security challenges were literally keeping me up at night and preventing us from closing crucial enterprise deals. edgefi transformed our approach to security from a box-checking exercise to a strategic advantage that actually accelerates our sales cycle.",
      author: "CEO",
      role: "BioMed Tech Company"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/TechStartupScalesSecurity"
    },
    tags: ["Healthcare", "Technology", "Compliance", "SOC 2", "Strategic Security", "Growth Enablement"],
    featured: true,
    detailedPage: true,
    redactedName: "MediCore Innovations"
  },
  
  // Managed services case studies
  {
    id: "legal-firm-practice-management",
    title: "Legal Firm Transforms Practice Management",
    industry: "Professional Services",
    companyType: "75+ Employees",
    challenge: "A mid-sized law firm was struggling with unreliable IT systems causing document management issues, poor client collaboration, and lack of secure mobile access that impacted attorney productivity and client satisfaction.",
    solution: "Comprehensive managed IT services with legal-specific application support",
    results: [
      { text: "Reduced IT-related downtime by 93% with proactive monitoring and support" },
      { text: "Implemented secure document management system with 99.99% uptime" },
      { text: "Enabled secure remote work capabilities for all attorneys and paralegals" },
      { text: "Achieved ABA cybersecurity compliance with enhanced protection for client data" }
    ],
    testimonial: {
      quote: "Our attorneys were spending 5-7 hours per week dealing with technology issues instead of billable work. edgefi's managed services eliminated these frustrations and gave us back nearly 350 billable hours per attorney annually.",
      author: "Managing Partner",
      role: "Regional Law Firm"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/legal-firm-practice-management"
    },
    tags: ["Professional Services", "Legal", "Document Management", "Productivity", "Managed Services"],
    featured: false,
    detailedPage: true,
    redactedName: "Northwest Legal Partners"
  },
  {
    id: "financial-services-business-continuity",
    title: "Regional Bank Ensures Business Continuity",
    industry: "Financial Services",
    companyType: "150+ Employees",
    challenge: "A regional financial services company experienced regular system instability and compliance concerns, causing transaction delays, security vulnerabilities, and regulatory risks that threatened customer trust and business operations.",
    solution: "Managed IT services with financial compliance focus and 24/7 monitoring",
    results: [
      { text: "Achieved 99.99% system availability with comprehensive disaster recovery" },
      { text: "Implemented multi-layered security meeting FFIEC/GLBA requirements" },
      { text: "Reduced security vulnerabilities by 87% through systematic patching" },
      { text: "Passed regulatory IT audits with zero critical findings" }
    ],
    testimonial: {
      quote: "In financial services, system reliability isn't just about convenience—it's about maintaining customer trust. edgefi understood our unique needs and delivered a managed IT solution that ensures we're always operational, secure, and compliant.",
      author: "COO",
      role: "Regional Financial Services Firm"
    },
    cta: {
      text: "See more details",
      link: "/case-studies/financial-services-business-continuity"
    },
    tags: ["Financial Services", "Compliance", "Business Continuity", "Disaster Recovery", "Managed Services"],
    featured: false,
    detailedPage: true,
    redactedName: "Pacific Financial Group"
  }
];
